import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingBag, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { scrollToId } from "./SmoothScroll";

const LINKS = [
  { label: "Skin", id: "skin" },
  { label: "Wellness", id: "wellness" },
  { label: "Weight", id: "program" },
  { label: "Research", id: "peptides" },
  { label: "Consult", id: "intake" },
  { label: "Wholesale", id: "wholesale" },
];

export default function Navbar({ onConsult }) {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [savedNudge, setSavedNudge] = useState(false);
  const firedRef = useRef(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Returning visitor with a persisted bag → gentle one-per-session nudge.
  // Ref guard + no cleanup keeps it resilient to React StrictMode double-invoke.
  useEffect(() => {
    if (firedRef.current) return;
    if (count > 0 && !sessionStorage.getItem("balance_nudge_seen")) {
      firedRef.current = true;
      sessionStorage.setItem("balance_nudge_seen", "1");
      setSavedNudge(true);
      setTimeout(() => setSavedNudge(false), 9000);
    }
  }, []); // eslint-disable-line

  const openBag = () => {
    setSavedNudge(false);
    setOpen(true);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 w-full z-[9000] transition-colors duration-500 ${
        scrolled ? "glass" : ""
      }`}
      data-testid="navbar"
    >
      <div className="max-w-[1500px] mx-auto px-5 sm:px-8 h-20 sm:h-24 flex items-center justify-between">
        <button
          onClick={() => scrollToId("top")}
          className="font-display text-4xl sm:text-5xl tracking-tight leading-none"
          data-testid="logo-btn"
        >
          Balance <span className="text-[var(--terra)]">+ Beauty</span>
        </button>

        <nav className="hidden lg:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => scrollToId(l.id)}
              className="overline text-[var(--ink)] hover:text-[var(--terra)] transition-colors"
              data-testid={`nav-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={onConsult}
            className="hidden sm:inline-flex btn-ink px-5 py-3 rounded-full"
            data-testid="nav-consult-btn"
          >
            Start consult
          </button>
          <button
            onClick={() => setOpen(true)}
            className="relative w-11 h-11 rounded-full border border-[var(--line)] flex items-center justify-center hover:bg-[var(--ink)] hover:text-[var(--bone)] transition-colors"
            aria-label="Open cart"
            data-testid="cart-open-btn"
          >
            <ShoppingBag size={17} strokeWidth={1.5} />
            {count > 0 && (
              <span
                className="absolute -top-1 -right-1 min-w-[19px] h-[19px] px-1 rounded-full bg-[var(--terra)] text-white text-[10px] font-mono flex items-center justify-center"
                data-testid="cart-count"
              >
                {count}
              </span>
            )}
            {savedNudge && (
              <span className="absolute -top-1 -right-1 min-w-[19px] h-[19px] rounded-full bg-[var(--terra)]/40 animate-ping" />
            )}
          </button>

          <AnimatePresence>
            {savedNudge && (
              <motion.div
                initial={{ opacity: 0, y: -8, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.96 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
                className="absolute right-4 sm:right-8 top-full mt-3 w-[268px] bg-[var(--bone)] border border-[var(--line)] shadow-xl p-4 z-[9001]"
                data-testid="saved-bag-nudge"
              >
                <button
                  onClick={() => setSavedNudge(false)}
                  className="absolute top-3 right-3 text-[var(--ink-soft)] hover:text-[var(--ink)]"
                  aria-label="Dismiss"
                  data-testid="saved-bag-nudge-close"
                >
                  <X size={14} />
                </button>
                <div className="flex items-start gap-3">
                  <span className="w-9 h-9 rounded-full bg-[var(--ink)] text-[var(--bone)] flex items-center justify-center shrink-0">
                    <ShoppingBag size={16} strokeWidth={1.5} />
                  </span>
                  <div>
                    <p className="font-display text-lg leading-tight">Your bag is waiting</p>
                    <p className="font-body text-xs text-[var(--ink-soft)] mt-1">
                      You saved {count} {count === 1 ? "item" : "items"} — pick up where you left off.
                    </p>
                    <button
                      onClick={openBag}
                      className="mt-3 overline px-4 py-2 bg-[var(--ink)] text-[var(--bone)] rounded-full hover:bg-[var(--terra)] transition-colors"
                      data-testid="saved-bag-nudge-view"
                    >
                      View bag
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.header>
  );
}
